/**
 * Browser-side GA4 + Meta Pixel CompleteRegistration / sign_up fires (gdn-1180).
 *
 * The same `event_id` is sent to the BE (which fires server-side GA4 MP + Meta
 * CAPI) AND to the browser tags here. Meta uses the matching event_id to dedupe
 * the browser pixel and CAPI events into a single CompleteRegistration with
 * EMQ > 7. GA4's server-side Measurement Protocol uses the event_id for
 * server-vs-browser dedupe; browser-side GA4 does not dedupe on this parameter,
 * so we must not emit the browser event more than once.
 *
 * The app uses Google Tag Manager (see index.html), so the canonical channel is
 * `window.dataLayer.push`. The direct `gtag`/`fbq` calls are fallbacks for when
 * GTM is absent — they are skipped if the dataLayer push already routed the
 * event, since GTM injects gtag.js itself. If no pathway is available, we warn
 * via Sentry so it surfaces as a follow-up ticket per the FE prompt in GDN-1180.
 */

import SentryService from '../SentryService';

import type { JpAttributionCookie } from './cookie';

declare global {
  // dataLayer's primary `any[]` declaration lives in src/utils/analytics_utils.tsx;
  // we only augment Window with the optional pixel functions here.
  interface Window {
    gtag?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
  }
}

export type FireBrowserPixelsArgs = {
  eventId: string;
  cookie: JpAttributionCookie | null;
};

export function fireBrowserSignupPixels({ eventId, cookie }: FireBrowserPixelsArgs): void {
  const utmSource = cookie?.utm_source;
  const utmMedium = cookie?.utm_medium;
  const utmCampaign = cookie?.utm_campaign;

  let routedThroughGtm = false;
  let routedThroughGtag = false;
  let routedThroughFbq = false;

  // Primary: Google Tag Manager dataLayer. GTM tags configured for GA4 sign_up
  // and Meta Pixel CompleteRegistration consume `event_id` to dedupe with the
  // server-side fires.
  if (typeof window !== 'undefined' && Array.isArray(window.dataLayer)) {
    window.dataLayer.push({
      event: 'sign_up',
      event_id: eventId,
      method: 'auth0',
      utm_source: utmSource,
      utm_medium: utmMedium,
      utm_campaign: utmCampaign,
    });
    window.dataLayer.push({
      event: 'CompleteRegistration',
      event_id: eventId,
      utm_source: utmSource,
      utm_medium: utmMedium,
      utm_campaign: utmCampaign,
    });
    routedThroughGtm = true;
  }

  // Direct gtag / fbq fallbacks — only when GTM didn't already pick them up.
  // GTM injects gtag.js for its GA4 tag, so window.gtag is typically defined
  // even when the dataLayer push has already routed the event. GA4 does NOT
  // dedupe browser-side events on event_id (that parameter is server-side MP
  // only), so an unconditional gtag call would double-count every signup.
  if (
    !routedThroughGtm &&
    typeof window !== 'undefined' &&
    typeof window.gtag === 'function'
  ) {
    window.gtag('event', 'sign_up', {
      event_id: eventId,
      method: 'auth0',
      source: utmSource,
      medium: utmMedium,
      campaign: utmCampaign,
    });
    routedThroughGtag = true;
  }
  if (
    !routedThroughGtm &&
    typeof window !== 'undefined' &&
    typeof window.fbq === 'function'
  ) {
    window.fbq('track', 'CompleteRegistration', {}, { eventID: eventId });
    routedThroughFbq = true;
  }

  // If none of these pathways exist, the browser side of the dedupe is broken —
  // server-side CAPI will still fire, but Meta won't have a browser event to
  // dedupe against, so EMQ stays low. Surface it loudly.
  if (!routedThroughGtm && !routedThroughGtag && !routedThroughFbq) {
    SentryService.captureMessage(
      'gdn-1180: signup pixels skipped — neither dataLayer, gtag, nor fbq is loaded',
      'warning',
    );
  }
}
